import { getSession, signOut } from 'next-auth/react';

import { AuthActionType } from '@redux/reducers/auth/enum';
import {
  changeUserInfo,
  clearProfile,
  createUserProfile,
  getUserProfile,
} from '@redux/reducers/profile/action';
import { setCountry } from '@redux/reducers/country/action';

import AnalyticsManager from '@utils/analyticsManager';
import { addUserDetailsCountly } from '@utils/analytics';
import { isCN } from '@utils/origin';
import { removeCancelRenewalData } from '@utils/localStorage';

import axios from 'axios';
import { clearBasket } from '../basket/action';
import { env } from 'next-runtime-env';
import { errorHandling, loadApplications } from '../applications/action';

export const setSessionId = (sessionId: string) => ({
  type: AuthActionType.SetSessionId,
  payload: sessionId,
});

export const setBearer = (bearer: string, expires: string) => ({
  type: AuthActionType.SetBearer,
  payload: { bearer, expires: new Date(expires) },
});

export const clearAuth = () => ({
  type: AuthActionType.ClearAuth,
});

export const fullLogout = () => dispatch => {
  if (isCN) {
    dispatch(changeUserInfo());
    removeCancelRenewalData();
    dispatch(logout());
  } else {
    dispatch(fullLogoutWithoutRedirect());
  }
};


export const fullLogoutWithoutRedirect = () => async dispatch => {
  const issuer = env('NEXT_PUBLIC_KEYCLOAK_LOGOUT');
  const clientId = 'gme-completeanatomy-webstore';
  const postLogoutRedirectUri = `${window.location.origin}/`;
  const session = await getSession();
  const idToken = session?.idToken;

  if (issuer) {
    let logoutUrl = `${issuer}/protocol/openid-connect/logout?post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;
    if (idToken) {
      logoutUrl += `&id_token_hint=${idToken}`;
    } else {
      logoutUrl += `&client_id=${clientId}`;
    }
    removeCancelRenewalData();
    dispatch(clearAuth());
    dispatch(clearProfile());
    dispatch(clearBasket());
    await signOut({ redirect: false });
    window.location.href = logoutUrl;

    return;
  }
};

export const logout = () => dispatch => {
  AnalyticsManager.shared.subHeader.addLogout();
  AnalyticsManager.googleLegacy.subHeader.addLogout();

  signOut();

  dispatch(clearAuth());
  dispatch(clearProfile());
  dispatch(clearBasket());

  try {
    dispatch(loadApplications());
  } catch (error) {
    dispatch(errorHandling(error));
  }
};

export const login =
  (getApplications = true, authCallbackUrl?: string) =>
  async dispatch => {
    AnalyticsManager.shared.subHeader.addLogin();
    AnalyticsManager.googleLegacy.subHeader.addLogin();

    let userProfile;

    try {
      userProfile = await dispatch(getUserProfile(authCallbackUrl));
    } catch (error) {
      dispatch(errorHandling(error));

      return Promise.reject(error);
    }

    if (!userProfile) {
      return Promise.reject();
    }

    addUserDetailsCountly(userProfile);

    if (getApplications) {
      try {
        await Promise.all([
          dispatch(
            setCountry(userProfile.updatedCountry ?? userProfile.country)
          ),
          dispatch(loadApplications()),
        ]);
      } catch (error) {
        dispatch(errorHandling(error));

        return Promise.reject();
      }
    }
  };

export const signUp =
  (getApplications = true) =>
  async dispatch => {
    AnalyticsManager.shared.subHeader.addLogin();
    AnalyticsManager.googleLegacy.subHeader.addLogin();

    let userProfile;

    try {
      userProfile = await dispatch(createUserProfile());
    } catch (error) {
      dispatch(errorHandling(error));

      return Promise.reject(error);
    }

    if (!userProfile) {
      return Promise.reject();
    }

    addUserDetailsCountly(userProfile);

    if (getApplications) {
      try {
        await Promise.all([
          dispatch(
            setCountry(userProfile.updatedCountry ?? userProfile.country)
          ),
          dispatch(loadApplications()),
        ]);
      } catch (error) {
        dispatch(errorHandling(error));

        return Promise.reject();
      }
    }
  };
